import React, { useState, useEffect } from 'react';
import {
  Text,
  Button,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  Select,
  Field,
  Card,
  makeStyles,
} from '@fluentui/react-components';
import {
  ChevronDown20Regular,
  ChevronRight20Regular,
  CheckmarkCircle20Filled,
  Circle20Regular,
  ArrowExport24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { surveysApi, surveyStatusApi, SurveyStatusResponse } from '../services/api.ts';
import PageHeader from '../components/PageHeader.tsx';
import StatCard from '../components/StatCard.tsx';
import StatusBadge from '../components/StatusBadge.tsx';
import EmptyState from '../components/EmptyState.tsx';
import Pagination from '../components/Pagination.tsx';
import { useToast } from '../components/Toast.tsx';

const MOBILE = '@media (max-width: 768px)';

function useIsMobile(): boolean {
  const query = '(max-width: 768px)';
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const useStyles = makeStyles({
  card: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-lg)',
    marginBottom: 'var(--space-lg)',
  },
  table: {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: '900px',
    [MOBILE]: {
      minWidth: 0,
    },
  },
  hideOnMobile: {
    [MOBILE]: {
      display: 'none',
    },
  },
  mobileMeta: {
    display: 'none',
    [MOBILE]: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-xs)',
      marginBottom: 'var(--space-md)',
      paddingBottom: 'var(--space-md)',
      borderBottom: '1px solid var(--color-border-light)',
      fontSize: '13px',
      color: 'var(--color-text-secondary)',
    },
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 'var(--space-md)',
    marginBottom: 'var(--space-lg)',
  },
  progressBar: {
    flex: 1,
    height: '6px',
    backgroundColor: 'var(--color-border-light)',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: 'var(--undp-primary)',
    transitionProperty: 'width',
    transitionDuration: '300ms',
    transitionTimingFunction: 'ease',
    borderRadius: '3px',
  },
  expandedRow: {
    backgroundColor: 'var(--color-background)',
    padding: 'var(--space-lg)',
  },
  responseCard: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-sm)',
    padding: 'var(--space-md)',
    marginBottom: 'var(--space-sm)',
  },
  responseBlock: {
    marginTop: 'var(--space-sm)',
    padding: 'var(--space-sm) var(--space-md)',
    borderRadius: 'var(--radius-sm)',
    borderLeft: '3px solid var(--color-border)',
  },
  responseBlockFilled: {
    marginTop: 'var(--space-sm)',
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'var(--undp-primary-light)',
    borderRadius: 'var(--radius-sm)',
    borderLeft: '3px solid var(--undp-primary)',
  },
  nameCell: {
    [MOBILE]: {
      cursor: 'pointer',
    },
  },
  expandHint: {
    display: 'none',
    [MOBILE]: {
      display: 'inline',
      marginLeft: 'var(--space-xs)',
      color: 'var(--color-text-tertiary)',
      fontSize: '12px',
    },
  },
  // Mobile-only popup (blurred backdrop + centered panel). Hidden on desktop.
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-lg)',
    zIndex: 1000,
    animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
    animationDuration: '0.18s',
    // Safety: this popup is a mobile affordance only.
    '@media (min-width: 769px)': {
      display: 'none',
    },
  },
  modalPanel: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    padding: 'var(--space-lg)',
    width: '100%',
    maxWidth: '420px',
    maxHeight: '85vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-md)',
    animationName: {
      from: { opacity: 0, transform: 'scale(0.96)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: '0.2s',
    animationTimingFunction: 'ease',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 'var(--space-sm)',
  },
  modalMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xs)',
    paddingBottom: 'var(--space-md)',
    borderBottom: '1px solid var(--color-border-light)',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
  },
});

type SurveyStatusUser = SurveyStatusResponse['users'][number];

const SurveyStatus: React.FC = () => {
  const s = useStyles();
  const toast = useToast();
  const isMobile = useIsMobile();

  const [surveys, setSurveys] = useState<any[]>([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);
  const [surveyStatus, setSurveyStatus] = useState<SurveyStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSurveys, setLoadingSurveys] = useState(true);
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());
  const [mobileUser, setMobileUser] = useState<SurveyStatusUser | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  useEffect(() => { setPage(1); }, [pageSize]);

  const totalUsers = surveyStatus?.users?.length ?? 0;

  // If the current page falls off the end after a status refresh, step back to the new last page.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalUsers / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [totalUsers, pageSize, page]);

  useEffect(() => {
    (async () => {
      try { setLoadingSurveys(true); setSurveys(await surveysApi.list()); }
      catch { toast.error('Failed to load surveys'); }
      finally { setLoadingSurveys(false); }
    })();
  }, []);

  const loadSurveyStatus = async (surveyId: number) => {
    try { setLoading(true); setSurveyStatus(await surveyStatusApi.getSurveyStatus(surveyId)); }
    catch { toast.error('Failed to load survey status'); }
    finally { setLoading(false); }
  };

  const handleSurveyChange = (surveyId: number) => {
    setSelectedSurveyId(surveyId);
    setExpandedUsers(new Set());
    setPage(1);
    loadSurveyStatus(surveyId);
  };

  const toggleUserExpansion = (key: number) => {
    const s = new Set(expandedUsers);
    if (s.has(key)) s.delete(key); else s.add(key);
    setExpandedUsers(s);
  };

  const handleExportToExcel = async () => {
    if (!selectedSurveyId) { toast.warning('Please select a survey first'); return; }
    try {
      const blob = await surveysApi.exportSurveyToExcel(selectedSurveyId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `survey_${(surveyStatus?.survey_title || 'export').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Failed to export'); }
  };

  const pagedUsers = (surveyStatus?.users ?? []).slice((page - 1) * pageSize, page * pageSize);

  const renderQuestionResponses = (us: SurveyStatusUser) => (
    us.questions.length === 0 ? (
      <Text size={300} style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>No questions in this survey.</Text>
    ) : (
      us.questions.map((q) => (
        <div key={q.question_id} className={s.responseCard}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
            <div style={{ marginTop: '2px' }}>
              {q.response_value ? <CheckmarkCircle20Filled style={{ color: 'var(--color-success)' }} /> : <Circle20Regular style={{ color: 'var(--color-border)' }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-sm)', marginBottom: '4px', flexWrap: 'wrap' }}>
                <Text size={300} weight="semibold">Q{q.question_order}: {q.question_text}</Text>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <StatusBadge status="info" label={q.question_type} />
                  {q.is_required && <StatusBadge status="danger" label="Required" />}
                  {q.declined && <StatusBadge status="warning" label="No reflection given" />}
                </div>
              </div>
              <div className={q.response_value ? s.responseBlockFilled : s.responseBlock}>
                {q.response_value ? (
                  <>
                    <Text size={300} style={{ display: 'block', marginBottom: '4px' }}>{q.response_value}</Text>
                    <div style={{ display: 'flex', gap: 'var(--space-md)', fontSize: '11px', color: 'var(--color-text-secondary)', flexWrap: 'wrap' }}>
                      {q.response_confidence !== null && <span>Confidence: <strong>{(q.response_confidence * 100).toFixed(1)}%</strong></span>}
                      {q.response_created_at && <span>Answered: <strong>{new Date(q.response_created_at).toLocaleString()}</strong></span>}
                    </div>
                  </>
                ) : (
                  <Text size={300} style={{ color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>
                    {q.declined ? 'No reflection given' : 'No response yet'}
                  </Text>
                )}
              </div>
            </div>
          </div>
        </div>
      ))
    )
  );

  return (
    <div>
      <PageHeader
        title="Survey Status"
        subtitle="View detailed status of survey responses for all assigned users"
        actions={surveyStatus ? <Button appearance="primary" icon={<ArrowExport24Regular />} onClick={handleExportToExcel}>Export</Button> : undefined}
      />

      {/* Survey selector */}
      <div className={s.card}>
        <div style={{ maxWidth: '500px', margin: '0 auto', width: '100%', minWidth: 0, overflow: 'hidden' }}>
          <Field label="Select Survey" required>
            {loadingSurveys ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px' }}><Spinner size="tiny" /><Text size={300}>Loading surveys...</Text></div>
            ) : (
              <Select style={{ width: '100%' }} value={selectedSurveyId?.toString() || ''} onChange={(_, data) => { const id = data.value ? parseInt(data.value) : null; if (id) handleSurveyChange(id); }}>
                <option value="">Choose a survey...</option>
                {surveys.map((sv) => (<option key={sv.id} value={sv.id.toString()}>{sv.title} ({sv.status})</option>))}
              </Select>
            )}
          </Field>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <Spinner size="large" />
          <Text style={{ marginTop: 'var(--space-md)', display: 'block', color: 'var(--color-text-secondary)' }}>Loading survey status...</Text>
        </div>
      )}

      {!loading && surveyStatus && (
        <>
          <div className={s.statsRow}>
            <StatCard title="Survey" value={surveyStatus.survey_title} />
            <StatCard title="Total Questions" value={surveyStatus.total_questions} />
            <StatCard title="Assigned Users" value={surveyStatus.total_assigned_users} />
          </div>

          {surveyStatus.users.length === 0 ? (
            <div className={s.card}><EmptyState title="No users assigned" description="No users have been assigned to this survey yet" /></div>
          ) : (
            <div className={s.card} style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-scroll">
              <Table className={s.table}>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className={s.hideOnMobile} style={{ width: '40px' }}></TableHeaderCell>
                    <TableHeaderCell style={{ width: '14%' }}>User</TableHeaderCell>
                    <TableHeaderCell className={s.hideOnMobile} style={{ width: '18%' }}>Email</TableHeaderCell>
                    <TableHeaderCell className={s.hideOnMobile} style={{ width: '8%' }}>Session</TableHeaderCell>
                    <TableHeaderCell className={s.hideOnMobile} style={{ width: '14%' }}>Project</TableHeaderCell>
                    <TableHeaderCell style={{ width: '10%' }}>Status</TableHeaderCell>
                    <TableHeaderCell style={{ width: '16%' }}>Progress</TableHeaderCell>
                    <TableHeaderCell className={s.hideOnMobile} style={{ width: '12%' }}>Assigned</TableHeaderCell>
                    <TableHeaderCell className={s.hideOnMobile} style={{ width: '12%' }}>Completed</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedUsers.map((us, index) => {
                    const rowKey = us.user_id * 1000 + (us.session_number || 0);
                    const isExpanded = expandedUsers.has(rowKey);
                    return (
                      <React.Fragment key={`${us.user_id}-${us.session_number || 0}-${index}`}>
                        <TableRow onClick={() => isMobile ? setMobileUser(us) : toggleUserExpansion(rowKey)} style={{ cursor: 'pointer', backgroundColor: isExpanded ? 'var(--color-background)' : 'transparent' }}>
                          <TableCell className={s.hideOnMobile}>
                            <Button appearance="subtle" size="small" icon={isExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />} />
                          </TableCell>
                          <TableCell className={s.nameCell}>
                            <Text weight="semibold" size={300}>{us.user_name}</Text>
                            <span className={s.expandHint}>›</span>
                          </TableCell>
                          <TableCell className={s.hideOnMobile}><Text size={300} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }} title={us.user_email}>{us.user_email}</Text></TableCell>
                          <TableCell className={s.hideOnMobile}><Text size={300}>{us.session_number ? `${us.session_number}${us.total_sessions ? ` of ${us.total_sessions}` : ''}` : '-'}</Text></TableCell>
                          <TableCell className={s.hideOnMobile}><Text size={300} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }} title={us.subject_label || undefined}>{us.subject_label || '-'}</Text></TableCell>
                          <TableCell><StatusBadge status={us.assignment_status} /></TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div className={`${s.progressBar} ${s.hideOnMobile}`}><div className={s.progressFill} style={{ width: `${us.progress_percentage}%` }} /></div>
                              <Text size={300} weight="semibold" style={{ minWidth: '50px', textAlign: 'right' }}>{us.answered_questions}/{us.total_questions}</Text>
                              {us.declined_questions > 0 && (
                                <Text size={200} style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>+{us.declined_questions} skipped</Text>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className={s.hideOnMobile}><Text size={300}>{new Date(us.assigned_at).toLocaleDateString()}</Text></TableCell>
                          <TableCell className={s.hideOnMobile}><Text size={300}>{us.completed_at ? new Date(us.completed_at).toLocaleDateString() : '-'}</Text></TableCell>
                        </TableRow>

                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={9} style={{ padding: 0 }}>
                              <div className={s.expandedRow}>
                                <div className={s.mobileMeta}>
                                  <span>Email: {us.user_email}</span>
                                  <span>Session: {us.session_number ? `${us.session_number}${us.total_sessions ? ` of ${us.total_sessions}` : ''}` : '-'}</span>
                                  <span>Project: {us.subject_label || '-'}</span>
                                  <span>Progress: {us.answered_questions}/{us.total_questions} ({us.progress_percentage}%)</span>
                                  <span>Assigned: {new Date(us.assigned_at).toLocaleDateString()}</span>
                                  <span>Completed: {us.completed_at ? new Date(us.completed_at).toLocaleDateString() : '-'}</span>
                                </div>
                                <Text weight="semibold" size={400} style={{ marginBottom: 'var(--space-md)', display: 'block' }}>Question Responses</Text>
                                {renderQuestionResponses(us)}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
              <div style={{ padding: '0 var(--space-md) var(--space-md)' }}>
                <Pagination total={surveyStatus.users.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }} />
              </div>
            </div>
          )}
        </>
      )}

      {!loading && !surveyStatus && (
        <div className={s.card}>
          <EmptyState title="Select a survey" description="Choose a survey above to view its status" />
        </div>
      )}

      {/* Mobile-only user details popup */}
      {mobileUser && (
        <div className={s.modalOverlay} onClick={() => setMobileUser(null)}>
          <div className={s.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <Text weight="semibold" size={400}>{mobileUser.user_name}</Text>
              <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} aria-label="Close" onClick={() => setMobileUser(null)} />
            </div>
            <div><StatusBadge status={mobileUser.assignment_status} /></div>
            <div className={s.modalMeta}>
              <span>Email: {mobileUser.user_email}</span>
              <span>Session: {mobileUser.session_number ? `${mobileUser.session_number}${mobileUser.total_sessions ? ` of ${mobileUser.total_sessions}` : ''}` : '-'}</span>
              <span>Project: {mobileUser.subject_label || '-'}</span>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-md)' }}>
                <span>Assigned: {new Date(mobileUser.assigned_at).toLocaleDateString()}</span>
                <span>Progress: {mobileUser.answered_questions}/{mobileUser.total_questions} ({mobileUser.progress_percentage}%)</span>
              </div>
              <span>Completed: {mobileUser.completed_at ? new Date(mobileUser.completed_at).toLocaleDateString() : '-'}</span>
            </div>
            <Text weight="semibold" size={400} style={{ display: 'block' }}>Question Responses</Text>
            {renderQuestionResponses(mobileUser)}
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyStatus;
